import React from 'react';
import { CheckCircle2, AlertCircle, Clock, Truck } from 'lucide-react';

// Modern UI Components for consistent styling across all Inbound Tool pages

// Modern Button Component
export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  onClick,
  disabled = false,
  ...props
}) => {
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variants = {
    primary: 'bg-gray-900 hover:bg-gray-800 text-white focus:ring-gray-500',
    secondary: 'bg-gray-100 hover:bg-gray-200 text-gray-900 focus:ring-gray-500',
    outline: 'border-2 border-gray-300 bg-white hover:bg-gray-50 text-gray-700 focus:ring-gray-500',
    ghost: 'hover:bg-gray-100 text-gray-700 focus:ring-gray-500',
    danger: 'bg-red-600 hover:bg-red-700 text-white focus:ring-red-500'
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm ',
    md: 'px-4 py-2 text-sm ',
    lg: 'px-6 py-3 text-base '
  };

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={onClick}
      disabled={disabled}
      {...props}
    >
      {children}
    </button>
  );
};

// Modern Input Component
export const Input = ({
  placeholder,
  value,
  onChange,
  icon: Icon,
  className = '',
  ...props
}) => {
  return (
    <div className="relative">
      {Icon && (
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Icon className="h-4 w-4 text-gray-400" />
        </div>
      )}
      <input
        type="text"
        className={`block w-full ${Icon ? 'pl-10' : 'pl-3'} pr-3 py-2 border-2 border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-900 bg-white transition-all duration-150 font-medium ${className}`}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        {...props}
      />
    </div>
  );
};

// Modern Badge Component
export const Badge = ({ children, variant = 'default', className = '' }) => {
  const variants = {
    default: 'bg-gray-100 text-gray-700',
    success: 'bg-green-100 text-green-700',
    warning: 'bg-yellow-100 text-yellow-700',
    error: 'bg-red-100 text-red-700',
    info: 'bg-blue-100 text-blue-700'
  };

  return (
    <span className={`inline-flex items-center px-2.5 py-0.5 -full text-xs font-medium ${variants[variant]} ${className}`}>
      {children}
    </span>
  );
};

// Modern Card Component
export const Card = ({ children, className = '', onClick, ...props }) => {
  return (
    <div
      className={`bg-white border border-gray-200 shadow-sm ${onClick ? 'cursor-pointer hover:shadow-md hover:border-gray-900 transition-all duration-150' : ''} ${className}`}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  );
};

// Modern Select Component
export const Select = ({
  value,
  onChange,
  options = [],
  placeholder = 'Select...',
  className = ''
}) => {
  return (
    <select
      value={value}
      onChange={onChange}
      className={`block w-full px-3 py-2 border-2 border-gray-300 bg-white text-gray-900 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:border-gray-900 transition-all duration-150 font-medium ${className}`}
    >
      <option value="">{placeholder}</option>
      {options.map((option, index) => (
        <option key={index} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
  );
};

// Loading Spinner Component
export const LoadingSpinner = ({ size = 'md', className = '' }) => {
  const sizes = {
    sm: 'w-4 h-4',
    md: 'w-8 h-8',
    lg: 'w-12 h-12'
  };

  return (
    <div className={`animate-spin -full border-2 border-gray-300 border-t-gray-900 ${sizes[size]} ${className}`}></div>
  );
};

// Status configuration helper
export const getStatusConfig = (status) => {
  const configs = {
    'received': { variant: 'success', label: 'Received', icon: CheckCircle2 },
    'shipped': { variant: 'info', label: 'Shipped', icon: Truck },
    'open': { variant: 'warning', label: 'Open', icon: AlertCircle },
    'processing': { variant: 'info', label: 'Processing', icon: Clock },
    default: { variant: 'default', label: status || 'Unknown', icon: Clock }
  };
  return configs[status?.toLowerCase()] || configs.default;
};

// Notification Component
export const Notification = ({ show, message, type = 'success', onClose }) => {
  if (!show) return null;

  const types = {
    success: 'bg-green-100 text-green-800 border-green-200',
    error: 'bg-red-100 text-red-800 border-red-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    info: 'bg-blue-100 text-blue-800 border-blue-200'
  };

  return (
    <div className={`fixed top-4 right-4 z-50 p-4 border shadow-lg ${types[type]}`}>
      <div className="flex items-center justify-between">
        <span>{message}</span>
        {onClose && (
          <button onClick={onClose} className="ml-4 text-current opacity-70 hover:opacity-100">
            ×
          </button>
        )}
      </div>
    </div>
  );
};